import { Component, Event, EventEmitter, Fragment, h, Host, Method, Prop, State, Watch } from '@stencil/core'
import Axios from 'axios'
import { startCase } from 'lodash-es'
import { flashToast } from 'src/app/toast'
import { isHighlight, isScreenShare } from 'src/bookmarks/types'
import { INote } from 'src/call/types'
import { IconName } from 'src/icons'
import { dayjs } from 'src/lib/dayjs'
import { css } from 'src/lib/emotion'
import { CallStore, isActionItemCompleted, markActionItem } from 'src/page/call/detail/state'
import { FontSizeType } from 'src/tailwind'
import { InlineIconText } from 'src/ui/components'
import { Menu } from 'src/ui/menu/menu'
import { classes, colorClasses } from 'src/utils/css'
import { formatDuration } from 'src/utils/date'

const editable = css`
  &:empty {
    &:focus {
      min-width: 1ch;
    }
    &:before {
      content: attr(data-placeholder);
    }
  }
`

@Component({
  tag: 'call-detail-notes-note-input',
})
export class LivePanelNotesInput {
  @Prop({ mutable: true }) note: Partial<INote>
  @Prop() canEdit = true
  @Prop() startTime: string
  @Prop() summarySize: FontSizeType = '2xs'
  @Prop() timestampSize: FontSizeType = 'xs'
  @Prop() hoverIcon: IconName

  @Event({ bubbles: false }) edited: EventEmitter<Partial<INote>>

  @State() showFailureText = false
  @State() editHover = false
  @State() hoverEnabled = true
  @State() placeholderTitle: string
  @State() actionItemIsChecked = false

  inputEl: HTMLUiTextInputElement
  menuEl: any

  showFailureTextTimeout: number
  updateShowFailureText() {
    if (
      !this.showFailureText &&
      !this.showFailureTextTimeout &&
      !this.note.summary &&
      this.note.text_generation_completed_at
    ) {
      const generationCompletedAt = dayjs(this.note.text_generation_completed_at).add(dayjs.duration({ seconds: 10 }))
      const now = dayjs()

      const canShow = generationCompletedAt.diff(now) > 0
      if (canShow) {
        this.showFailureText = true
        this.showFailureTextTimeout = window.setTimeout(() => this.updateShowFailureText(), 10000)
        return
      }
    }

    if (this.showFailureTextTimeout) {
      this.showFailureText = false
      clearTimeout(this.showFailureTextTimeout)
    }
  }

  @Method()
  async isFocused() {
    // Have to do this rather than blocking the onClick propagation because that
    // does not work with the empty text field on clicking the placeholder
    return !this.hoverEnabled
  }

  @Watch('note')
  onNote() {
    this.updateShowFailureText()
    this.placeholderTitle = startCase(this.note.bookmark.label.toLowerCase())
    this.actionItemIsChecked = isActionItemCompleted(this.note.id)
  }

  componentWillLoad() {
    this.onNote()
  }

  render() {
    const secsElapsed = this.startTime ? dayjs(this.note.timestamp).diff(this.startTime, 's') : 0
    const duration = this.note.end_time - this.note.start_time
    const timestamp = this.note.bookmark.highlight
      ? `- ${formatDuration(duration)}`
      : dayjs.duration(secsElapsed, 's').format(secsElapsed >= 3600 ? '@ H:mm:ss' : '@ m:ss')
    const screenShare = isScreenShare(this.note.bookmark)
    const color = isHighlight(this.note.bookmark) ? colorClasses({ text: this.note.bookmark.hex_color }) : 'text-white'
    const probablyGenerated = this.note.text_auto_generated || this.note.summary === this.note.text
    const showPlaceholder = probablyGenerated || !this.note.text || this.note.text?.length === 0
    const showHoverIcon = !!this.hoverIcon && !this.note.bookmark.action_item

    return (
      <Host class={`flex flex-row ${color} max-w-full`} key={`summary-any-bookmark-${this.note.id}`}>
        <aside class='flex flex-col justify-center items-center mr-2'>
          {/* Use hidden= swapping to have seamless hover transfers */}
          <ui-icon hidden={!showHoverIcon} icon={this.hoverIcon} size='base' />
          {/* Do not show spinner on call view when the item is an action item so we have a checkbox */}
          {this.note.text_generation_in_progress && !this.note.bookmark.action_item ? (
            <ui-spinner hidden={showHoverIcon} strokeWeight='3px' class='text-brand' />
          ) : this.note.bookmark.highlight ? (
            <ui-icon hidden={showHoverIcon} icon='play-button-thick' class='mt-2px' />
          ) : this.note.bookmark.bookmark ? (
            <ui-icon hidden={showHoverIcon} icon='bookmark-outline-thick' class='mt-2px' />
          ) : this.note.bookmark.action_item ? (
            <ui-checkbox
              highlight={false}
              hidden={showHoverIcon}
              checked={this.actionItemIsChecked}
              disabled={!this.canEdit}
              onChecked={({ detail }) => {
                this.actionItemIsChecked = detail
                markActionItem(this.note.id, this.actionItemIsChecked)
              }}
              onClick={(e) => {
                // Block propagation to playClip
                e.stopPropagation()
              }}
            />
          ) : (
            screenShare && <ui-icon hidden={showHoverIcon} icon='display' size='base' />
          )}
          <div class='mt-auto'></div>
        </aside>

        <div class='flex flex-col spacing-y-3px' style={{ width: 'calc(100% - 15px)' }}>
          <div class='inline w-full max-w-full'>
            {this.canEdit ? (
              <Fragment>
                <ui-text-input
                  class={`
                    inline border-dotted border-opacity-0 border-white border-b-2 cursor-text
                    leading-19px
                    ${classes(this.hoverEnabled, 'hover:border-opacity-30')}
                    ${classes(this.actionItemIsChecked, 'line-through')}
                    ${editable}
                  `}
                  key={`note-text-input-${this.note.id}`}
                  ref={(el) => (this.inputEl = el)}
                  filledClass='font-extrabold'
                  value={showPlaceholder ? '' : this.note.text}
                  onTextChanged={({ detail }) => {
                    this.edited.emit({ ...this.note, text: detail })
                  }}
                  autoFocus={false}
                  emptyClass='font-extrabold'
                  placeholder={this.placeholderTitle}
                  emptyFocusedClass='italic font-extrabold text-info-warn'
                  focusedPlaceholder='&nbsp;'
                  debounceMilliseconds={1000}
                  defaultStyle={false}
                  onGainedFocus={(details) => (this.hoverEnabled = !details.detail)}
                  onMouseEnter={() => (this.editHover = true)}
                  onMouseLeave={() => (this.editHover = false)}
                />

                {this.hoverEnabled && (
                  <Fragment>
                    {this.note.bookmark.action_item && this.note.auto_generated && ' ✨'}
                    <span
                      class={`
                        text-glyph-lighter whitespace-nowrap leading-none text-${this.timestampSize}
                        ${classes(this.editHover, 'hidden', 'inline')}
                      `}
                    >
                      &#32;{timestamp}
                    </span>
                    <ui-icon
                      icon='pencil-fill'
                      class={`
                        ml-1 text-xs
                        ${classes(this.editHover, 'inline', 'hidden')}
                      `}
                    />
                    {this.note.bookmark.action_item &&
                      !!this.note.actions?.assignActionItemUrl &&
                      !!CallStore.state.actionItemAssignment?.candidateAssignees && (
                        <button
                          class={`
                            text-${this.timestampSize} font-medium leading-none text-glyph-lighter spacing-x-3px pl-8px
                            ${classes(this.editHover, 'hidden', 'inline')}
                          `}
                          onClick={(e) => {
                            // Block propagation to playClip
                            e.stopPropagation()
                          }}
                        >
                          {this.note.actionItemAssigneeName ? (
                            <InlineIconText class='h-15px text-info-warn' icon='person' spacingX='2px'>
                              {this.note.actionItemAssigneeName}
                            </InlineIconText>
                          ) : (
                            <InlineIconText class='h-15px' icon='person-plus' spacingX='3px'>
                              Assign
                            </InlineIconText>
                          )}
                          <Menu ref={(el) => (this.menuEl = el)} allowClick={false} contentWidth='48' on='click'>
                            <action-item-assignment-menu
                              assigneeName={this.note.actionItemAssigneeName}
                              candidateAssignees={CallStore.state.actionItemAssignment.candidateAssignees}
                              onAssign={async ({ detail }) => {
                                this.menuEl?.close()
                                try {
                                  await Axios.post(this.note.actions.assignActionItemUrl, {
                                    assignee_type: detail.assignee_type,
                                    assignee_id: detail.assignee_id,
                                  })
                                  this.note = {
                                    ...this.note,
                                    actionItemAssigneeName: detail.name,
                                  }
                                } catch (ex) {
                                  flashToast({ type: 'alert', text: `Failed to assign action item to ${detail.name}` })
                                }
                              }}
                            />
                          </Menu>
                        </button>
                      )}
                  </Fragment>
                )}
              </Fragment>
            ) : (
              <Fragment>
                <span class='inline font-extrabold'>{showPlaceholder ? this.placeholderTitle : this.note.text}</span>
                {this.note.bookmark.action_item && this.note.auto_generated && ' ✨'}
                <span class={`inline text-glyph-lighter whitespace-nowrap text-${this.timestampSize}`}>
                  &#32;{timestamp}
                </span>
              </Fragment>
            )}
          </div>

          {this.note.summary ? (
            <span class={`font-medium leading-125% text-glyph-base break-words text-${this.summarySize}`}>
              {this.note.summary}
            </span>
          ) : this.note.text_generation_in_progress ? (
            <span class={`italic font-medium leading-125% text-glyph-lighter text-${this.summarySize}`}>
              Summarizing {this.placeholderTitle}...
            </span>
          ) : (
            this.showFailureText && (
              <span class={`italic font-medium leading-125% text-glyph-lighter text-${this.summarySize}`}>
                AI failed to summarize
              </span>
            )
          )}
        </div>
      </Host>
    )
  }
}
